import dayjs, { type Dayjs } from 'dayjs';
import type { DashboardGranularity } from '../types/dashboardStats';

export type PeriodMode = 'preset' | 'custom';

export type PresetDef = {
  key: string;
  label: string;
  from: () => Dayjs;
  to: () => Dayjs;
};

export const DASHBOARD_PRESETS: Record<DashboardGranularity, PresetDef[]> = {
  hour: [
    { key: '7d', label: '7 jours', from: () => dayjs().subtract(6, 'day').startOf('day'), to: () => dayjs().endOf('day') },
    { key: '14d', label: '14 jours', from: () => dayjs().subtract(13, 'day').startOf('day'), to: () => dayjs().endOf('day') },
    { key: '30d', label: '30 jours', from: () => dayjs().subtract(29, 'day').startOf('day'), to: () => dayjs().endOf('day') },
  ],
  day: [
    { key: '7d', label: '7 jours', from: () => dayjs().subtract(6, 'day').startOf('day'), to: () => dayjs().endOf('day') },
    { key: '14d', label: '14 jours', from: () => dayjs().subtract(13, 'day').startOf('day'), to: () => dayjs().endOf('day') },
    { key: '30d', label: '30 jours', from: () => dayjs().subtract(29, 'day').startOf('day'), to: () => dayjs().endOf('day') },
  ],
  week: [
    { key: '4w', label: '4 semaines', from: () => dayjs().subtract(3, 'week').startOf('week'), to: () => dayjs().endOf('week') },
    { key: '8w', label: '8 semaines', from: () => dayjs().subtract(7, 'week').startOf('week'), to: () => dayjs().endOf('week') },
    { key: '12w', label: '12 semaines', from: () => dayjs().subtract(11, 'week').startOf('week'), to: () => dayjs().endOf('week') },
  ],
  month: [
    { key: '3m', label: '3 mois', from: () => dayjs().subtract(2, 'month').startOf('month'), to: () => dayjs().endOf('month') },
    { key: '6m', label: '6 mois', from: () => dayjs().subtract(5, 'month').startOf('month'), to: () => dayjs().endOf('month') },
    { key: '12m', label: '12 mois', from: () => dayjs().subtract(11, 'month').startOf('month'), to: () => dayjs().endOf('month') },
  ],
};

export function formatDashboardPeriodLabel(period: string, granularity: DashboardGranularity): string {
  if (granularity === 'hour') {
    const hour = Number.parseInt(period, 10);
    if (Number.isNaN(hour)) return period;
    return `${String(hour).padStart(2, '0')}h`;
  }
  const d = dayjs(period);
  if (!d.isValid()) return period;
  if (granularity === 'month') return d.format('MMM YYYY');
  if (granularity === 'week') return `Sem. ${d.format('D MMM')}`;
  return d.format('D MMM');
}

export function formatDashboardRangeLabel(from: string, to: string): string {
  const a = dayjs(from);
  const b = dayjs(to);
  if (!a.isValid() || !b.isValid()) return `${from} → ${to}`;
  return `${a.format('D MMM YYYY')} → ${b.format('D MMM YYYY')}`;
}

export function buildDashboardDateRange(
  granularity: DashboardGranularity,
  periodMode: PeriodMode,
  presetKey: string,
  customFrom: Dayjs | null,
  customTo: Dayjs | null,
): { date_from: string; date_to: string } {
  if (periodMode === 'custom' && customFrom && customTo) {
    return {
      date_from: customFrom.format('YYYY-MM-DD'),
      date_to: customTo.format('YYYY-MM-DD'),
    };
  }
  const preset =
    DASHBOARD_PRESETS[granularity].find((p) => p.key === presetKey) ??
    DASHBOARD_PRESETS[granularity][DASHBOARD_PRESETS[granularity].length - 1];
  return {
    date_from: preset.from().format('YYYY-MM-DD'),
    date_to: preset.to().format('YYYY-MM-DD'),
  };
}
